import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Star, Lock } from 'react-feather';
import axios from 'axios';
import config from '../../../config';

// Plan IDs that mean the user is already on a Lifetime or First Mover plan
// and should NOT see the migration offer. Anyone on these plans is already
// at the best available rate (or has already claimed Lifetime).
const ALREADY_LOCKED_IN_PLAN_IDS = [
  // Lifetime
  'Legacy-Subscriber---Lifetime',
  // Legacy First Mover SKUs (kept for backward compat — these were sold
  // before May 2026 and we no longer offer them, but anyone holding one
  // already has their rate locked in and shouldn't be re-pitched).
  'First-Mover-Network-Monthly',
  'First-Mover-Network-Annual',
  'First-Mover-Verified-Monthly',
  'First-Mover-Verified-Annual',
  'viveka-network-firstmover',
  'viveka-verified-lifetime-firstmover',
  'Legacy-Subscriber---Grandfathered',
];

// Dismissal timestamp key. Stored in localStorage so it survives
// browser sessions. The popup reappears once the stored timestamp is
// more than 30 days old, giving coaches repeated but non-spammy touches.
const SESSION_KEY = 'viveka:migrationPopupShown';
const SHOW_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * SubscriptionMigrationBanner (modal popup variant)
 *
 * Pop-up shown once per browser session to early-member coaches whose
 * Sharetribe accounts pre-date the May 1, 2026 cutoff and who haven't
 * yet claimed Lifetime. Frames the offer as a thank-you for being part
 * of the network from the start, surfaces the Lifetime opportunity, and
 * notes that standard monthly/annual remain available unchanged.
 *
 * Render conditions (popup eligible when ALL are true):
 *   - currentUser is loaded
 *   - currentUser is a coach (isProvider === true)
 *   - createdAt is before config.firstMoverCutoffDate (early member)
 *   - subscriptionName is NOT already a Lifetime or First Mover SKU
 *   - sessionStorage flag has NOT been set (this browser session)
 *
 * The CTA navigates to /account/payments/pricing — eligible coaches see
 * the early-member panel with Network/Verified at standard pricing plus
 * Lifetime as a third card.
 */

const SPARKLES = [
  { top: '10%', left: '6%',  size: 14, color: '#FBBF24', delay: '0s',   duration: '3s'   },
  { top: '18%', left: '20%', size: 8,  color: '#FFFFFF', delay: '0.6s', duration: '2.4s' },
  { top: '78%', left: '10%', size: 10, color: '#FCD34D', delay: '1.2s', duration: '2.8s' },
  { top: '40%', left: '28%', size: 7,  color: '#FBBF24', delay: '1.8s', duration: '2.2s' },
  { top: '88%', left: '34%', size: 12, color: '#FFFFFF', delay: '0.3s', duration: '3.2s' },
  { top: '8%',  left: '52%', size: 10, color: '#FBBF24', delay: '0.9s', duration: '2.6s' },
  { top: '62%', left: '50%', size: 8,  color: '#FCD34D', delay: '1.5s', duration: '3s'   },
  { top: '22%', left: '66%', size: 14, color: '#FFFFFF', delay: '0.4s', duration: '2.8s' },
  { top: '85%', left: '70%', size: 9,  color: '#FBBF24', delay: '1.1s', duration: '2.4s' },
  { top: '14%', left: '88%', size: 11, color: '#FCD34D', delay: '0.7s', duration: '3s'   },
  { top: '50%', left: '94%', size: 7,  color: '#FFFFFF', delay: '1.6s', duration: '2.6s' },
  { top: '90%', left: '90%', size: 12, color: '#FBBF24', delay: '0.2s', duration: '3.2s' },
];

const SparkleStar = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'block' }} aria-hidden="true">
    <path d="M12 2 L13.5 9 L20 10.5 L13.5 12 L12 19 L10.5 12 L4 10.5 L10.5 9 Z" fill={color} />
  </svg>
);

// Lifetime hero card — the only offer in this popup. Gold border,
// "VIVEKA FIRST 100" badge, $997 one-time, "no renewals ever" framing,
// and live seat counter for urgency.
const LifetimeHeroCard = ({ seatsRemaining }) => {
  const seatsCopy =
    typeof seatsRemaining === 'number'
      ? seatsRemaining > 0
        ? `Only ${seatsRemaining} of 100 positions remaining`
        : 'All 100 positions claimed'
      : 'Limited to the first 100 early members';
  const isSoldOut = typeof seatsRemaining === 'number' && seatsRemaining <= 0;

  return (
    <div
      style={{
        position: 'relative',
        padding: '28px 28px 24px',
        borderRadius: '16px',
        background:
          'radial-gradient(circle at 100% 0%, rgba(251, 191, 36, 0.18) 0%, rgba(251, 191, 36, 0) 50%), ' +
          'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%)',
        border: '2px solid rgba(251, 191, 36, 0.6)',
        boxShadow: '0 10px 32px rgba(251, 191, 36, 0.18)',
        textAlign: 'left',
        opacity: isSoldOut ? 0.65 : 1,
      }}
    >
      {!isSoldOut && (
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '4px 12px',
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
            color: '#1E1040',
            fontSize: '10px',
            fontWeight: 900,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            boxShadow: '0 4px 12px rgba(251, 191, 36, 0.45)',
            whiteSpace: 'nowrap',
          }}
        >
          ★ Pay Once · Lifetime
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '4px 10px',
              borderRadius: '999px',
              background: 'rgba(251, 191, 36, 0.18)',
              color: '#FBBF24',
              fontSize: '10px',
              fontWeight: 800,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: '14px',
            }}
          >
            <Lock size={10} strokeWidth={2.5} />
            Viveka First 100
          </div>
          <div
            style={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#FFFFFF',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}
          >
            Verified Coach · Lifetime
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: '44px',
                fontWeight: 900,
                color: '#FBBF24',
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              $997
            </span>
            <span style={{ fontSize: '13px', color: '#CBD5E1', fontWeight: 600 }}>
              one-time
            </span>
          </div>
          <div
            style={{
              fontSize: '10.5px',
              fontWeight: 800,
              letterSpacing: '0.08em',
              color: '#FBBF24',
              marginTop: '8px',
              textTransform: 'uppercase',
            }}
          >
            No renewals · ever
          </div>
        </div>

        <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              fontSize: '12.5px',
              color: '#E5E7EB',
              lineHeight: 1.7,
            }}
          >
            <li>
              <span style={{ color: '#FBBF24', fontWeight: 800, marginRight: '8px' }}>✓</span>
              All Verified Coach features, for life
            </li>
            <li>
              <span style={{ color: '#FBBF24', fontWeight: 800, marginRight: '8px' }}>✓</span>
              Locked-in price · no future increases
            </li>
            <li>
              <span style={{ color: '#FBBF24', fontWeight: 800, marginRight: '8px' }}>✓</span>
              Founder badge on your profile
            </li>
            <li>
              <span style={{ color: '#FBBF24', fontWeight: 800, marginRight: '8px' }}>✓</span>
              Enterprise opportunities + spotlight
            </li>
          </ul>

          <div
            style={{
              marginTop: '14px',
              padding: '8px 12px',
              borderRadius: '8px',
              background: isSoldOut
                ? 'rgba(107, 114, 128, 0.18)'
                : 'rgba(251, 191, 36, 0.14)',
              border: isSoldOut
                ? '1px dashed rgba(107, 114, 128, 0.5)'
                : '1px dashed rgba(251, 191, 36, 0.55)',
              color: isSoldOut ? '#94A3B8' : '#FCD34D',
              fontSize: '11.5px',
              fontWeight: 700,
              letterSpacing: '0.02em',
              textAlign: 'center',
            }}
          >
            {seatsCopy}
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionMigrationBanner = ({ currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [seatsRemaining, setSeatsRemaining] = useState(null);

  const isProvider = currentUser?.attributes?.profile?.publicData?.isProvider;
  const subscriptionName =
    currentUser?.attributes?.profile?.metadata?.subscriptionDetails?.subscriptionName;
  const createdAt = currentUser?.attributes?.createdAt;

  // Early-member eligibility: account pre-dates the GRANDFATHER_CUTOFF_DATE.
  const isLegacyEligible = (() => {
    const cutoff = config.firstMoverCutoffDate;
    if (!cutoff || !createdAt) return false;
    return new Date(createdAt) < new Date(cutoff);
  })();

  // Only show the popup if the coach is an eligible early member AND
  // they haven't already claimed Lifetime (or a legacy First Mover SKU).
  const isEligible =
    !!currentUser &&
    isProvider === true &&
    isLegacyEligible &&
    !(subscriptionName && ALREADY_LOCKED_IN_PLAN_IDS.indexOf(subscriptionName) !== -1);

  useEffect(() => {
    if (!isEligible) return;
    try {
      const lastShown = window.localStorage.getItem(SESSION_KEY);
      const shouldShow = !lastShown || Date.now() - Number(lastShown) > SHOW_INTERVAL_MS;
      if (shouldShow) setIsOpen(true);
    } catch (e) {
      setIsOpen(true);
    }
  }, [isEligible]);

  // Fetch the live Lifetime seat count when the popup opens. Non-fatal
  // if it fails — copy degrades to the generic "first 100 coaches" line.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    axios
      .post(
        `${config.subscriptionServiceUrl}eligible-plans`,
        { createdAt },
        { headers: { 'content-type': 'application/json' } }
      )
      .then(res => {
        if (cancelled) return;
        if (res.data && res.data.lifetime && typeof res.data.lifetime.seatsRemaining === 'number') {
          setSeatsRemaining(res.data.lifetime.seatsRemaining);
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [isOpen, createdAt]);

  // Escape key closes the popup.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeydown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Lock body scroll while the popup is open.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    try {
      window.localStorage.setItem(SESSION_KEY, String(Date.now()));
    } catch (e) { /* localStorage unavailable; popup will re-open on next mount */ }
  };

  const handleBackdropClick = () => handleClose();

  if (!isEligible || !isOpen) return null;

  return (
    <React.Fragment>
      <style>{`
        @keyframes migrationPopupTwinkle {
          0%, 100% { opacity: 0; transform: scale(0.3) rotate(0deg); }
          50%      { opacity: 1; transform: scale(1) rotate(180deg); }
        }
        @keyframes migrationPopupShimmer {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        @keyframes migrationPopupPulseGlow {
          0%, 100% {
            box-shadow:
              0 4px 20px rgba(251, 191, 36, 0.45),
              0 0 0 0 rgba(251, 191, 36, 0.4);
          }
          50% {
            box-shadow:
              0 6px 28px rgba(251, 191, 36, 0.6),
              0 0 0 8px rgba(251, 191, 36, 0);
          }
        }
        @keyframes migrationPopupFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes migrationPopupSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="migration-popup-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          animation: 'migrationPopupFadeIn 0.25s ease-out',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '620px',
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            padding: '36px 32px',
            borderRadius: '20px',
            background:
              'radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.32) 0%, rgba(139, 92, 246, 0) 55%), ' +
              'linear-gradient(135deg, #064E3B 0%, #0F3F4A 45%, #1E1040 100%)',
            backgroundSize: '200% 200%',
            animation:
              'migrationPopupShimmer 14s ease-in-out infinite, migrationPopupSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.55)',
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'background 0.15s ease, transform 0.15s ease',
              zIndex: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
              e.currentTarget.style.transform = 'scale(1.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <X size={18} strokeWidth={2.5} />
          </button>

          {/* Sparkle confetti layer */}
          <div
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', borderRadius: '20px' }}
            aria-hidden="true"
          >
            {SPARKLES.map((s, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: s.top,
                  left: s.left,
                  animation: `migrationPopupTwinkle ${s.duration} ease-in-out ${s.delay} infinite`,
                  opacity: 0,
                }}
              >
                <SparkleStar size={s.size} color={s.color} />
              </div>
            ))}
          </div>

          <div style={{ position: 'relative', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 14px',
                borderRadius: '999px',
                border: '1px solid rgba(251, 191, 36, 0.7)',
                color: '#FBBF24',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: '16px',
                background: 'rgba(251, 191, 36, 0.08)',
              }}
            >
              <Star size={11} fill="#FBBF24" stroke="none" />
              The First 100
              <Star size={11} fill="#FBBF24" stroke="none" />
            </div>

            <h2
              id="migration-popup-title"
              style={{
                margin: 0,
                fontSize: '30px',
                fontWeight: 800,
                lineHeight: 1.15,
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
              }}
            >
              Welcome back.
            </h2>

            <p
              style={{
                margin: '14px auto 0',
                color: '#E5E7EB',
                fontSize: '15px',
                lineHeight: 1.6,
                maxWidth: '500px',
              }}
            >
              You joined Viveka before the relaunch. As one of our early members, you can lock in{' '}
              <strong style={{ color: '#FBBF24', fontWeight: 800 }}>lifetime Verified Coach access</strong>{' '}
              — a position kept for the first 100.
            </p>

            <div style={{ marginTop: '26px' }}>
              <LifetimeHeroCard seatsRemaining={seatsRemaining} />
            </div>

            <div
              style={{
                marginTop: '18px',
                fontSize: '12.5px',
                color: '#94A3B8',
                lineHeight: 1.55,
                textAlign: 'center',
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              Monthly and annual Verified Coach membership remain available at standard pricing.
            </div>

            <div
              style={{
                marginTop: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Link
                to="/account/payments/pricing"
                onClick={handleClose}
                style={{
                  backgroundColor: '#FBBF24',
                  color: '#064E3B',
                  fontWeight: 800,
                  padding: '14px 28px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: 'none',
                  animation: 'migrationPopupPulseGlow 2.4s ease-in-out infinite',
                  transition: 'transform 0.18s ease, filter 0.18s ease',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
                  e.currentTarget.style.filter = 'brightness(1.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                <Star size={15} fill="#064E3B" stroke="none" />
                Lock In Lifetime Access
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>

              <button
                type="button"
                onClick={handleClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  color: '#94A3B8',
                  fontSize: '13px',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#E2E8F0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(SubscriptionMigrationBanner);
